import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getScheduleEventsByJob,
  deleteScheduleEvent,
  updateScheduleEvent,
  notifyScheduleEvent,
} from '@/lib/api/schedule';
import type { ScheduleEvent as ScheduleEventType } from '@/lib/api/schedule';
import { JobSchedulingForm } from '@/components/jobs/JobSchedulingForm';
import { updateScheduledJob, getJobById, type JobScheduleData } from '@/lib/api/jobs';
import { WorkerAssignPanel } from '@/components/scheduling/WorkerAssignPanel';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { ToastContainer } from '@/components/common/Toast';
import { useToast } from '@/lib/hooks/useToast';

interface ScheduleTabProps {
  jobId: string;
  jobDetails?: {
    startDate?: string;
    endDate?: string;
    title?: string;
    jobNumber?: string;
    status?: string;
  };
}

export const ScheduleTab = ({ jobId, jobDetails }: ScheduleTabProps) => {
  const navigate = useNavigate();
  const [events, setEvents] = useState<ScheduleEventType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showEditScheduleForm, setShowEditScheduleForm] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);
  const [fullJobDetails, setFullJobDetails] = useState<any>(null);
  const [notifyReceipt, setNotifyReceipt] = useState<{ text: string; ok: boolean } | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | string | null>(null);
  const { toasts, removeToast, toast } = useToast();

  useEffect(() => {
    loadSchedule();
    loadFullJobDetails();
  }, [jobId]);

  const loadFullJobDetails = async () => {
    try {
      const details = await getJobById(jobId);
      setFullJobDetails(details);
    } catch (error) {
      console.error('Failed to load full job details:', error);
    }
  };

  const loadSchedule = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getScheduleEventsByJob(jobId);
      setEvents(data);
    } catch (error) {
      console.error('Failed to load schedule:', error);
      setError('Failed to load schedule. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (eventId: number | string, newStatus: string) => {
    try {
      setActionLoading(`status-${eventId}`);
      await updateScheduleEvent(eventId, { status: newStatus });
      await loadSchedule();
      toast.success('Status updated.');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update status.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteEvent = async (eventId: number | string) => {
    setConfirmDeleteId(eventId);
  };

  const confirmDeleteEvent = async () => {
    if (confirmDeleteId === null) return;
    const id = confirmDeleteId;
    setConfirmDeleteId(null);
    try {
      setActionLoading(`delete-${id}`);
      await deleteScheduleEvent(id);
      await loadSchedule();
      toast.success('Schedule entry deleted.');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete schedule entry.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleNotifyClient = async (eventId: number | string) => {
    try {
      setActionLoading(`notify-${eventId}`);
      const result = await notifyScheduleEvent(eventId);
      const text = result.emailed
        ? `Schedule reminder sent to ${result.emailTo || 'client'} · Copy saved to Sent folder`
        : `Could not send reminder: ${result.reason || 'no client email on record'}`;
      setNotifyReceipt({ text, ok: result.emailed });
      setTimeout(() => setNotifyReceipt(null), 7000);
    } catch {
      setNotifyReceipt({ text: 'Failed to send reminder. Please try again.', ok: false });
      setTimeout(() => setNotifyReceipt(null), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-CA', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-700';
      case 'in_progress':
        return 'bg-blue-100 text-blue-700';
      case 'scheduled':
        return 'bg-purple-100 text-purple-700';
      case 'cancelled':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'check_circle';
      case 'in_progress':
        return 'pending';
      case 'scheduled':
        return 'event';
      case 'cancelled':
        return 'cancel';
      default:
        return 'schedule';
    }
  };

  const getStatusLabel = (status: string) => {
    return status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
          <p className="text-gray-600">Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-red-500 mb-4">error</span>
          <p className="text-gray-900 font-semibold mb-2">Failed to load schedule</p>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={loadSchedule}
            className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const scheduledEvents = events.filter(e => e.status === 'scheduled');
  const inProgressEvents = events.filter(e => e.status === 'in_progress');
  const completedEvents = events.filter(e => e.status === 'completed');

  const calculateDuration = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const handleEditTask = (taskId: string) => {
    setEditingTaskId(taskId);
    setShowEditScheduleForm(true);
  };

  const handleAddNewTask = () => {
    setEditingTaskId('__new__'); // Special marker for new task
    setShowEditScheduleForm(true);
  };

  const handleUpdateSchedule = async (data: JobScheduleData) => {
    try {
      setUpdating(true);
      await updateScheduledJob(jobId, data);
      setShowEditScheduleForm(false);
      setEditingTaskId(null);
      await loadSchedule();
      toast.success('Schedule updated.');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      toast.error(err?.response?.data?.message || err?.message || 'Failed to update schedule.');
    } finally {
      setUpdating(false);
    }
  };

  const duration = calculateDuration(jobDetails?.startDate, jobDetails?.endDate);

  return (
    <div>
      {notifyReceipt && (
        <div className={`flex items-center gap-2 mb-4 px-4 py-3 rounded-lg border text-sm ${
          notifyReceipt.ok
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}>
          <span className={`material-symbols-outlined text-base ${notifyReceipt.ok ? 'text-green-600' : 'text-amber-600'}`}>
            {notifyReceipt.ok ? 'check_circle' : 'warning'}
          </span>
          {notifyReceipt.text}
        </div>
      )}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-gray-900 text-[22px] font-bold leading-tight tracking-[-0.015em]">
            Schedule
          </h2>
          <p className="text-gray-600 text-sm mt-1">
            {events.length} scheduled {events.length === 1 ? 'entry' : 'entries'} for this job
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleAddNewTask}
            className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Add New Task
          </button>
          <button
            onClick={() => navigate(`/calendar?jobId=${jobId}`)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-lg transition-colors"
          >
            <span className="material-symbols-outlined text-sm">calendar_month</span>
            View Full Calendar
          </button>
        </div>
      </div>

      {/* Job Timeline Block */}
      {jobDetails && jobDetails.startDate && jobDetails.endDate && 
       jobDetails.startDate.trim() !== '' && jobDetails.endDate.trim() !== '' ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
          <div className="bg-primary/5 p-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <span className="material-symbols-outlined text-primary text-2xl">work</span>
              </div>
              <div className="flex-1">
                <h3 className="text-gray-900 font-bold text-lg">
                  Job Timeline
                </h3>
                <p className="text-gray-600 text-sm">
                  Overall job duration from start to completion
                </p>
              </div>
            </div>
          </div>
          
          <div className="p-6">
            <div className="flex items-center gap-4 mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="material-symbols-outlined text-green-600 text-sm">play_circle</span>
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">Start Date</span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {formatDate(jobDetails.startDate)}
                </p>
              </div>
              
              <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-lg">
                <span className="material-symbols-outlined text-primary">schedule</span>
                <span className="text-sm font-semibold text-primary">
                  {duration} {duration === 1 ? 'day' : 'days'}
                </span>
              </div>
              
              <div className="flex-1 text-right">
                <div className="flex items-center justify-end gap-2 mb-2">
                  <span className="text-xs font-medium text-gray-600 uppercase tracking-wide">End Date</span>
                  <span className="material-symbols-outlined text-red-600 text-sm">stop_circle</span>
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {formatDate(jobDetails.endDate)}
                </p>
              </div>
            </div>
            
            {/* Visual Timeline Bar */}
            <div className="relative">
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full transition-all ${
                    jobDetails.status?.toLowerCase() === 'completed' 
                      ? 'bg-green-500' 
                      : jobDetails.status?.toLowerCase() === 'in_progress'
                      ? 'bg-blue-500'
                      : 'bg-primary'
                  }`}
                  style={{ 
                    width: jobDetails.status?.toLowerCase() === 'completed' 
                      ? '100%' 
                      : jobDetails.status?.toLowerCase() === 'in_progress'
                      ? '50%'
                      : '0%'
                  }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>Project start</span>
                <span>Project completion</span>
              </div>
            </div>
          </div>
        </div>
      ) : jobDetails ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-2">
            <span className="material-symbols-outlined text-yellow-600">info</span>
            <p className="text-sm text-yellow-800">
              Job timeline not available. Please set start and end dates for this job to see the timeline.
            </p>
          </div>
        </div>
      ) : null}

      {/* Stats Cards */}
      {events.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-gray-600 text-sm">event</span>
              <p className="text-gray-600 text-sm">Total</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{events.length}</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-purple-500 text-sm">schedule</span>
              <p className="text-gray-600 text-sm">Scheduled</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{scheduledEvents.length}</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-blue-500 text-sm">pending</span>
              <p className="text-gray-600 text-sm">In Progress</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{inProgressEvents.length}</p>
          </div>
          
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="flex items-center gap-2 mb-2">
              <span className="material-symbols-outlined text-green-500 text-sm">check_circle</span>
              <p className="text-gray-600 text-sm">Completed</p>
            </div>
            <p className="text-2xl font-bold text-gray-900">{completedEvents.length}</p>
          </div>
        </div>
      )}

      {events.length === 0 && (!jobDetails?.startDate || !jobDetails?.endDate || 
        jobDetails.startDate.trim() === '' || jobDetails.endDate.trim() === '') ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">
            calendar_month
          </span>
          <p className="text-gray-900 font-semibold mb-2">No Schedule Information</p>
          <p className="text-gray-600 mb-6">
            This job has not been scheduled yet. Set start and end dates to begin scheduling.
          </p>
        </div>
      ) : events.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <span className="material-symbols-outlined text-6xl text-gray-400 mb-4">
            task_alt
          </span>
          <p className="text-gray-900 font-semibold mb-2">No Scheduled Tasks</p>
          <p className="text-gray-600 mb-6">
            The job is scheduled but no specific tasks have been added yet.
          </p>
          <button
            onClick={() => navigate(`/calendar?jobId=${jobId}`)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
          >
            <span className="material-symbols-outlined text-sm">add</span>
            Add Tasks to Calendar
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {(() => {
            // Group events by task name to consolidate multiday tasks into single blocks
            const groupedEvents = new Map<string, ScheduleEventType[]>();
            
            events.forEach(event => {
              const key = event.notes || `event-${event.id}`;
              if (!groupedEvents.has(key)) {
                groupedEvents.set(key, []);
              }
              groupedEvents.get(key)!.push(event);
            });

            return Array.from(groupedEvents.values()).map((taskEvents) => {
              // Use the first event as representative, but calculate date range from all
              const event = taskEvents[0];
              const dates = taskEvents.map(e => e.scheduled_date || e.scheduledDate).sort();
              const startDate = dates[0];
              const endDate = dates[dates.length - 1];
              const isMultiday = startDate !== endDate;
              
              return (
                <div
                  key={event.id}
                  className="bg-white rounded-xl p-4 border border-gray-200 hover:border-primary transition-colors"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                        <span className="material-symbols-outlined text-primary">task_alt</span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-gray-900 font-bold">
                            {event.notes || 'Task'}
                          </h3>
                          <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(event.status)}`}>
                            <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>{getStatusIcon(event.status)}</span>
                            <span>{getStatusLabel(event.status)}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-600">
                          <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">calendar_month</span>
                            <span>
                              {isMultiday 
                                ? `${formatDate(startDate)} - ${formatDate(endDate)}`
                                : formatDate(event.scheduled_date || event.scheduledDate)
                              }
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">schedule</span>
                            <span>
                              {formatTime(event.start_time || event.startTime || '')} - {formatTime(event.end_time || event.endTime || '')}
                            </span>
                          </div>
                          {isMultiday && (
                            <span className="text-xs text-gray-500">
                              ({dates.filter(date => {
                                const day = new Date(date).getDay();
                                return day !== 0 && day !== 6;
                              }).length} working days)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

              <div className="flex items-center gap-2 mb-3 pl-15">
                <span className="material-symbols-outlined text-gray-600 text-sm">
                  groups
                </span>
                <span className="text-gray-600 text-sm">
                  Crew: <span className="text-gray-900 font-medium">{event.crew_name || event.crewName || 'Unassigned'}</span>
                </span>
              </div>

              {/* Individual worker assignments */}
              <details className="mb-3 pl-15">
                <summary className="cursor-pointer text-sm font-semibold text-primary flex items-center gap-1.5 select-none border border-primary/30 bg-primary/5 rounded-lg px-3 py-2 w-fit hover:bg-primary/10">
                  <span className="material-symbols-outlined text-base">manage_accounts</span>
                  Assign workers to this day
                </summary>
                <div className="mt-2 ml-6">
                  <WorkerAssignPanel
                    scheduleId={Number(event.id)}
                    scheduledDate={event.scheduled_date || event.scheduledDate}
                  />
                </div>
              </details>

              {/* Subtasks */}
              {event.subtasks && event.subtasks.length > 0 && (
                <div className="mb-3 pl-15">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <span className="material-symbols-outlined text-sm">subdirectory_arrow_right</span>
                    Sub-tasks ({event.subtasks.length})
                  </h4>
                  <div className="space-y-2">
                    {event.subtasks.map((subtask: any) => (
                      <div key={subtask.id} className="bg-white border border-gray-200 rounded-lg p-3">
                        <div className="flex items-start justify-between mb-2">
                          <h5 className="text-sm font-medium text-gray-900">{subtask.title}</h5>
                          {subtask.start_date && (
                            <span className="text-xs text-gray-500">
                              {new Date(subtask.start_date).toLocaleDateString()}
                              {subtask.end_date && ` - ${new Date(subtask.end_date).toLocaleDateString()}`}
                            </span>
                          )}
                        </div>
                        
                        {/* Checklist Items */}
                        {subtask.checklistItems && subtask.checklistItems.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-gray-100">
                            <p className="text-xs font-medium text-gray-600 mb-1">Checklist:</p>
                            <div className="space-y-1">
                              {subtask.checklistItems.map((item: any) => (
                                <div key={item.id} className="flex items-center gap-2 text-sm">
                                  <input
                                    type="checkbox"
                                    checked={item.checked}
                                    readOnly
                                    className="rounded border-gray-300 text-primary focus:ring-primary"
                                  />
                                  <span className={item.checked ? 'line-through text-gray-400' : 'text-gray-700'}>
                                    {item.text}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-2 pt-3 border-t border-gray-200">
                {event.status === 'scheduled' && (
                  <button
                    onClick={() => handleUpdateStatus(event.id, 'in_progress')}
                    disabled={actionLoading === `status-${event.id}`}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === `status-${event.id}` ? (
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-sm">play_arrow</span>
                        Start
                      </>
                    )}
                  </button>
                )}

                {event.status === 'in_progress' && (
                  <button
                    onClick={() => handleUpdateStatus(event.id, 'completed')}
                    disabled={actionLoading === `status-${event.id}`}
                    className="flex items-center justify-center gap-2 px-3 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actionLoading === `status-${event.id}` ? (
                      <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-sm">check_circle</span>
                        Complete
                      </>
                    )}
                  </button>
                )}

                <button
                  onClick={() => handleNotifyClient(event.id)}
                  disabled={actionLoading === `notify-${event.id}`}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Email client a schedule reminder"
                >
                  {actionLoading === `notify-${event.id}` ? (
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700" />
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-sm">mail</span>
                      Notify Client
                    </>
                  )}
                </button>

                <button
                  onClick={() => handleEditTask(event.id.toString())}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-900 rounded-lg transition-colors text-sm"
                >
                  <span className="material-symbols-outlined text-sm">edit</span>
                  Edit
                </button>

                <button
                  onClick={() => handleDeleteEvent(event.id)}
                  disabled={actionLoading === `delete-${event.id}`}
                  className="flex items-center justify-center gap-2 px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {actionLoading === `delete-${event.id}` ? (
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-red-700"></div>
                  ) : (
                    <>
                      <span className="material-symbols-outlined text-sm">delete</span>
                      Delete
                    </>
                  )}
                </button>
              </div>
            </div>
              );
            });
          })()}
        </div>
      )}

      {/* Edit Schedule Form Modal */}
      {showEditScheduleForm && fullJobDetails && (
        <JobSchedulingForm
          isOpen={showEditScheduleForm}
          onClose={() => {
            setShowEditScheduleForm(false);
            setEditingTaskId(null);
          }}
          onSubmit={handleUpdateSchedule}
          isSubmitting={updating}
          initialData={{
            id: jobId,
            title: fullJobDetails.title,
            jobNumber: fullJobDetails.jobNumber || fullJobDetails.job_number,
            startDate: fullJobDetails.startDate || fullJobDetails.start_date,
            estimatedEndDate: fullJobDetails.endDate || fullJobDetails.end_date || fullJobDetails.estimatedEndDate || fullJobDetails.estimated_end_date,
            clientId: fullJobDetails.clientId || fullJobDetails.client_id,
            client: fullJobDetails.client || fullJobDetails.clientName || fullJobDetails.client_name,
            siteAddress: fullJobDetails.siteAddress || fullJobDetails.site_address,
          }}
          isEditMode={true}
          editingTaskId={editingTaskId}
        />
      )}

      {confirmDeleteId !== null && (
        <ConfirmDialog
          title="Delete Schedule Entry"
          message="Are you sure you want to delete this schedule entry? This cannot be undone."
          confirmLabel="Delete"
          variant="danger"
          onConfirm={confirmDeleteEvent}
          onCancel={() => setConfirmDeleteId(null)}
        />
      )}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};


